import { forwardRef, PropsWithChildren, useMemo, useRef } from 'react';

import cx from 'classnames';

import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
} from '../shadcn/Dialog';
import { useModal } from './useModal';
import type { TLayer } from './ModalContext';
import { useModalLayer } from './ModalLayerContext';
import { useAdaptive } from '../../hooks';
import { TValueOf } from '@skillarea/lib-shared';
import { Icon } from '../icons/Icon';

export const EModalSizes = {
  m: 'm',
  l: 'l',
  fullwidth: 'fullwidth',
  fullscreen: 'fullscreen',
};

type TModalSizes = TValueOf<typeof EModalSizes>;

interface IProps {
  classNameCloser?: string;
  classNameModal?: string;
  layer?: TLayer;
  isAutoClosing?: boolean;
  isBlocking?: boolean;
  onBeforeClose?: () => void;
  size?: TModalSizes;
  isOpen?: boolean;
  /** Accessibility: title for screen readers */
  title?: string;
  /** Accessibility: description for screen readers */
  description?: string;
  hideCloseButton?: boolean;
  preventOutsideClose?: boolean;
}

const TOAST_LAYER_SELECTOR =
  '[data-sonner-toaster], [data-sonner-toast], .toaster';

function getInteractOutsideTarget(event: Event): EventTarget | null {
  const eventWithDetail = event as Event & {
    detail?: {
      originalEvent?: Event;
    };
  };

  return eventWithDetail.detail?.originalEvent?.target ?? event.target;
}

function isToastLayerTarget(target: EventTarget | null): boolean {
  if (typeof Element === 'undefined' || !(target instanceof Element))
    return false;
  return Boolean(target.closest(TOAST_LAYER_SELECTOR));
}

export const Modal = forwardRef<HTMLDivElement, PropsWithChildren<IProps>>(
  function Modal(
    {
      children,
      classNameCloser,
      classNameModal,
      layer,
      isAutoClosing = true,
      isBlocking = true,
      onBeforeClose,
      size = EModalSizes.m,
      isOpen = true,
      title,
      description,
      hideCloseButton = false,
      preventOutsideClose = false,
    },
    forwardedRef,
  ) {
    const { closeModal } = useModal();
    const contextLayer = useModalLayer();
    const { isDesktop } = useAdaptive();
    const isHandlingCloseRef = useRef(false);
    const resolvedLayer = layer ?? contextLayer ?? 2;
    const contentAccessibilityProps = description
      ? {}
      : ({ 'aria-describedby': undefined } as const);

    const handleClose = () => {
      if (isHandlingCloseRef.current) return;

      isHandlingCloseRef.current = true;
      globalThis.setTimeout(() => {
        isHandlingCloseRef.current = false;
      }, 0);

      onBeforeClose?.();
      if (isAutoClosing) closeModal();
    };

    const sizeClass = useMemo(() => {
      const sizeMap: Record<TModalSizes, string> = {
        m: 'max-w-lg',
        l: 'max-w-6xl',
        fullwidth: isDesktop
          ? 'w-full max-w-7xl'
          : 'w-[calc(100vw-16px)] max-w-none',
        fullscreen: 'w-screen h-screen max-w-none max-h-none rounded-none',
      };
      return sizeMap[size];
    }, [size, isDesktop]);

    return (
      <Dialog
        modal={isBlocking}
        open={isOpen}
        onOpenChange={(open) => !open && handleClose()}
      >
        <DialogContent
          ref={forwardedRef}
          layer={resolvedLayer}
          className={cx(sizeClass, classNameModal)}
          {...contentAccessibilityProps}
          onEscapeKeyDown={(e) => {
            if (!isAutoClosing || preventOutsideClose) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (
              preventOutsideClose ||
              isToastLayerTarget(getInteractOutsideTarget(e))
            ) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (
              preventOutsideClose ||
              isToastLayerTarget(getInteractOutsideTarget(e))
            ) {
              e.preventDefault();
            }
          }}
        >
          {!hideCloseButton && (
            <DialogClose asChild>
              <button
                className={cx(
                  'absolute z-50 flex h-10 w-10 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none',
                  'right-0 top-0',
                  classNameCloser,
                )}
                aria-label="Close dialog"
                onClick={handleClose}
                type="button"
              >
                <Icon color="contrast" img="close" />
              </button>
            </DialogClose>
          )}
          {title && <DialogTitle className="sr-only">{title}</DialogTitle>}
          {description && (
            <DialogDescription className="sr-only">
              {description}
            </DialogDescription>
          )}
          {children}
        </DialogContent>
      </Dialog>
    );
  },
);
